import { useEffect, useState } from "react";
import { endpoints, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { formatCurrency } from "../../lib/romEngine";
import { Truck, Buildings, Plus, Trash, CircleNotch } from "@phosphor-icons/react";

export default function Subcontractors() {
    const { user } = useAuth();
    const isManage = ["admin", "manager"].includes(user?.role);
    const [companies, setCompanies] = useState([]);
    const [rates, setRates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [newCompany, setNewCompany] = useState("");
    const [rateForm, setRateForm] = useState({ work_type: "", rate_per_hour: "" });
    const [saving, setSaving] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [c, r] = await Promise.all([endpoints.subContractorCompanies(), isManage ? endpoints.subcontractorRates() : Promise.resolve({ data: [] })]);
            setCompanies(c.data || []); setRates(r.data || []);
        } catch (e) { setErr(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, [isManage]);

    async function addCompany() {
        if (!newCompany.trim()) return;
        setSaving(true);
        try { await endpoints.createSubContractorCompany({ company_name: newCompany.trim() }); setNewCompany(""); await load(); }
        catch (e) { alert(formatApiError(e)); }
        finally { setSaving(false); }
    }

    async function saveRate() {
        if (!rateForm.work_type.trim()) { setErr("Work type is required"); return; }
        setSaving(true); setErr("");
        try {
            await endpoints.saveSubcontractorRate({ work_type: rateForm.work_type.trim(), rate_per_hour: Number(rateForm.rate_per_hour) || 0 });
            setRateForm({ work_type: "", rate_per_hour: "" });
            await load();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setSaving(false); }
    }

    async function deleteRate(id) {
        if (!window.confirm("Delete this rate?")) return;
        try { await endpoints.deleteSubcontractorRate(id); await load(); }
        catch (e) { alert(formatApiError(e)); }
    }

    async function deleteCompany(id) {
        if (!window.confirm("Remove this company? Only possible if no subcontractors are on file for it.")) return;
        try { await endpoints.deleteSubContractorCompany(id); await load(); }
        catch (e) { alert(formatApiError(e)); }
    }

    return (
        <div className="max-w-[1100px] mx-auto px-6 py-8 space-y-6" data-testid="subcontractors-page">
            <header>
                <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--rom-text-3)] font-semibold mb-1">Timesheets</div>
                <h1 className="font-display font-black text-3xl tracking-tight flex items-center gap-3">
                    <Truck size={28} weight="bold" className="text-[var(--rom-orange)]" /> Subcontractors
                </h1>
                <p className="text-sm text-[var(--rom-text-2)] mt-1">Companies who sign in on site, and the work-type rate table used to price their clock-in hours.</p>
            </header>

            {err && <div className="text-sm text-red-600" data-testid="subs-page-error">{err}</div>}

            <div className="rom-card">
                <div className="rom-card-head"><h3 className="flex items-center gap-2"><Buildings size={16} weight="bold" /> Companies</h3></div>
                <div className="rom-card-body space-y-3">
                    {isManage && (
                        <div className="flex gap-2 max-w-md">
                            <input data-testid="new-company-input" value={newCompany} onChange={(e) => setNewCompany(e.target.value)} placeholder="Add a company…" className={inputCls} />
                            <button data-testid="new-company-submit" onClick={addCompany} disabled={saving} className="btn btn-primary shrink-0"><Plus size={14} weight="bold" /></button>
                        </div>
                    )}
                    {loading ? (
                        <div className="text-center py-6 text-[var(--rom-text-3)] text-sm">Loading…</div>
                    ) : companies.length === 0 ? (
                        <div className="text-center py-6 text-[var(--rom-text-3)] text-sm" data-testid="companies-empty">No companies yet — added automatically when a subcontractor signs in on site, or add one above.</div>
                    ) : (
                        <div className="flex flex-wrap gap-2" data-testid="companies-list">
                            {companies.map((c) => (
                                <span key={c.id} className="chip chip-navy inline-flex items-center gap-1.5" data-testid={`company-chip-${c.id}`}>
                                    {c.company_name}
                                    {isManage && (
                                        <button onClick={() => deleteCompany(c.id)} data-testid={`company-delete-${c.id}`} className="hover:text-red-300" title="Remove company"><Trash size={11} weight="bold" /></button>
                                    )}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {isManage && (
                <div className="rom-card">
                    <div className="rom-card-head"><h3>Work-type rate table</h3><span className="text-xs text-[var(--rom-text-3)]">Applied automatically when a subcontractor clocks in</span></div>
                    <div className="rom-card-body space-y-4">
                        <div className="flex gap-2 flex-wrap items-end max-w-xl">
                            <div className="flex-1 min-w-[180px]">
                                <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">Work type</label>
                                <input data-testid="rate-work-type-input" value={rateForm.work_type} onChange={(e) => setRateForm((f) => ({ ...f, work_type: e.target.value }))} placeholder="e.g. Debris Removal" className={inputCls} />
                            </div>
                            <div className="w-32">
                                <label className="block text-xs font-semibold text-[var(--rom-text-2)] mb-1">$ / hr</label>
                                <input data-testid="rate-value-input" type="number" value={rateForm.rate_per_hour} onChange={(e) => setRateForm((f) => ({ ...f, rate_per_hour: e.target.value }))} className={inputCls} />
                            </div>
                            <button data-testid="rate-save-btn" onClick={saveRate} disabled={saving} className="btn btn-primary">
                                {saving ? <CircleNotch size={14} className="animate-spin" /> : <Plus size={14} weight="bold" />} Save rate
                            </button>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="rom-table">
                                <thead><tr><th>Work type</th><th className="numeric">Rate / hr</th><th></th></tr></thead>
                                <tbody>
                                    {rates.length === 0 ? (
                                        <tr><td colSpan={3} className="text-center py-8 text-[var(--rom-text-3)]">No rates yet.</td></tr>
                                    ) : rates.map((r) => (
                                        <tr key={r.id} data-testid={`rate-row-${r.id}`}>
                                            <td className="font-semibold">{r.work_type}</td>
                                            <td className="numeric font-mono">{formatCurrency(r.rate_per_hour)}</td>
                                            <td><button onClick={() => deleteRate(r.id)} data-testid={`rate-delete-${r.id}`} className="p-1.5 rounded text-red-500 hover:bg-red-50"><Trash size={14} /></button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

const inputCls = "w-full h-9 px-3 rounded-[4px] border border-[var(--rom-border)] text-sm focus:border-[var(--rom-orange)] focus:outline-none bg-white";
